import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuth } from "../../context/AuthContext";
import { IoPersonCircle, IoServerOutline, IoGitBranch, IoLogOut } from "react-icons/io5";

export default function Settings() {
  const { user, logout } = useAuth();

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            Platform Settings
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            Manage account information, webhook endpoints, and hosting system parameters
          </VuiTypography>
        </VuiBox>

        <Grid container spacing={3}>
          {/* User Account Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <VuiBox display="flex" alignItems="center" gap={2} mb={3}>
                <VuiBox
                  width="48px"
                  height="48px"
                  borderRadius="14px"
                  bgColor="info"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <IoPersonCircle color="#fff" size="30px" />
                </VuiBox>
                <VuiBox>
                  <VuiTypography variant="h5" color="white" fontWeight="bold">
                    {user?.name || "Account Profile"}
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text">
                    {user?.email || "user@intent.yt"}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>

              <VuiBox mb={2}>
                <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                  USER ID
                </VuiTypography>
                <VuiTypography variant="button" color="white">
                  #{user?.id || 1}
                </VuiTypography>
              </VuiBox>

              <VuiBox mb={2}>
                <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                  EMAIL ADDRESS
                </VuiTypography>
                <VuiTypography variant="button" color="white">
                  {user?.email}
                </VuiTypography>
              </VuiBox>

              <VuiBox mb={3}>
                <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                  ACCOUNT ROLE
                </VuiTypography>
                <VuiBadge color="info" variant="standard" size="sm" badgeContent={user?.role?.toUpperCase() || "ADMIN"} />
              </VuiBox>

              <VuiButton variant="gradient" color="error" onClick={logout}>
                <IoLogOut style={{ marginRight: "6px" }} /> Sign Out
              </VuiButton>
            </Card>
          </Grid>

          {/* GitHub Webhook Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <VuiBox display="flex" alignItems="center" gap={2} mb={3}>
                <VuiBox
                  width="48px"
                  height="48px"
                  borderRadius="14px"
                  bgColor="dark"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <IoGitBranch color="#fff" size="26px" />
                </VuiBox>
                <VuiBox>
                  <VuiTypography variant="h5" color="white" fontWeight="bold">
                    GitHub Webhook
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text">
                    Automated build triggers for external repos
                  </VuiTypography>
                </VuiBox>
              </VuiBox>

              <VuiTypography variant="button" color="text" display="block" mb={2}>
                To enable automatic deployments when you push to GitHub, add a webhook in your repository settings:
              </VuiTypography>

              <VuiBox p={2} mb={2} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "10px" }}>
                <VuiTypography variant="caption" color="info" fontWeight="bold" display="block" mb={0.5}>
                  PAYLOAD URL
                </VuiTypography>
                <VuiTypography variant="button" color="white" sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                  https://panel.web.int.yt/api/webhooks/github
                </VuiTypography>
              </VuiBox>

              <VuiBox p={2} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "10px" }}>
                <VuiTypography variant="caption" color="info" fontWeight="bold" display="block" mb={0.5}>
                  CONTENT TYPE
                </VuiTypography>
                <VuiTypography variant="button" color="white" sx={{ fontFamily: "monospace" }}>
                  application/json
                </VuiTypography>
              </VuiBox>
            </Card>
          </Grid>

          {/* System Infrastructure Status */}
          <Grid item xs={12}>
            <Card>
              <VuiBox display="flex" alignItems="center" gap={2} mb={3}>
                <VuiBox
                  width="48px"
                  height="48px"
                  borderRadius="14px"
                  bgColor="success"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <IoServerOutline color="#fff" size="26px" />
                </VuiBox>
                <VuiBox>
                  <VuiTypography variant="h5" color="white" fontWeight="bold">
                    Hosting Infrastructure Health
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text">
                    Isolated daemon health and resource limits
                  </VuiTypography>
                </VuiBox>
              </VuiBox>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <VuiBox p={2} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "10px" }}>
                    <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                      REST API SERVICE
                    </VuiTypography>
                    <VuiBadge color="success" variant="standard" size="sm" badgeContent="ACTIVE (127.0.0.1:4000)" />
                  </VuiBox>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <VuiBox p={2} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "10px" }}>
                    <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                      BUILD WORKER DAEMON
                    </VuiTypography>
                    <VuiBadge color="success" variant="standard" size="sm" badgeContent="ACTIVE (POLLING)" />
                  </VuiBox>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <VuiBox p={2} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "10px" }}>
                    <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                      REVERSE PROXY & EDGE
                    </VuiTypography>
                    <VuiBadge color="success" variant="standard" size="sm" badgeContent="NGINX 1.24 HTTP/2" />
                  </VuiBox>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>

      <Footer />
    </DashboardLayout>
  );
}
